import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { Toast } from "@/atoms";
import { ToastContext } from "@/hooks/shared/use-toast"; // eslint-disable-line code-style/absolute-imports-only -- internal hooks import
import type { ToastContextValueInterface, ToastInterface } from "@/interfaces";

export const ToastProvider = ({ children }: { children: ReactNode }) => {
    const [toasts, setToasts] = useState<ToastInterface[]>([]);

    const dismissHandler = useCallback(
        (id: string) => setToasts((prev) => prev.filter(({ id: toastId }) => toastId !== id)),
        [],
    );

    const showHandler = useCallback(
        (input: Omit<ToastInterface, "id">) => {
            const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;

            setToasts((prev) => [
                ...prev,
                {
                    ...input,
                    id,
                },
            ]);

            return id;
        },
        [],
    );

    const value = useMemo<ToastContextValueInterface>(
        () => ({
            onDismiss: dismissHandler,
            onShow: showHandler,
            toasts,
        }),
        [
            toasts,
            dismissHandler,
            showHandler,
        ],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div
                className="
                    pointer-events-none
                    fixed
                    top-4
                    right-2
                    left-2
                    z-50
                    flex
                    w-auto
                    flex-col
                    gap-2
                    sm:right-4
                    sm:left-auto
                    sm:w-full
                    sm:max-w-sm
                "
            >
                {toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        toast={toast}
                        onDismiss={dismissHandler}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};
